import { z } from "zod";

/**
 * The manager palette.
 *
 * A board card carries a soft tint of the colour of the manager who owns it, so a meeting
 * reads ownership at a glance without reading a name. Curated rather than a free picker:
 * fourteen keys whose legibility is verified **once for the system** — see
 * `colors.test.ts`, which composites every entry over both themes' card surfaces at the
 * fixed alphas and asserts the text still clears WCAG AA. A hex picker would make that a
 * per-user QA task nobody would perform.
 *
 * **Nine, not fourteen, and that number was measured rather than chosen.** A tint strong
 * enough to separate more colours puts the card's secondary text below AA-large; a tint
 * weak enough to keep it comfortable separates fewer. Nine is where those two curves meet
 * at 24%/30%, verified in oklab — the space the browser actually mixes in. An earlier
 * attempt claimed fourteen on the strength of an sRGB approximation, under which Rose and
 * Red differ by ΔE 0.006: indistinguishable on screen.
 *
 * The tint is an at-a-glance **aid, not the accessible carrier of ownership**: the owner's
 * name stays in the card's accessible label, on the detail modal, in the Owner column and
 * in the owner filter. A colour-blind reader loses the glance, never a capability.
 */
export const MANAGER_COLORS = [
  { key: "rose", label: "Rose", hex: "#e11d48" },
  { key: "amber", label: "Amber", hex: "#d97706" },
  { key: "lime", label: "Lime", hex: "#65a30d" },
  { key: "emerald", label: "Emerald", hex: "#059669" },
  { key: "cyan", label: "Cyan", hex: "#0891b2" },
  { key: "blue", label: "Blue", hex: "#2563eb" },
  { key: "violet", label: "Violet", hex: "#7c3aed" },
  { key: "fuchsia", label: "Fuchsia", hex: "#c026d3" },
  { key: "stone", label: "Stone", hex: "#57534e" },
] as const;

export type ManagerColor = (typeof MANAGER_COLORS)[number]["key"];

export const MANAGER_COLOR_KEYS = MANAGER_COLORS.map((color) => color.key) as [ManagerColor, ...ManagerColor[]];

export const managerColorSchema = z.enum(MANAGER_COLOR_KEYS);

/**
 * Narrow a stored value to a palette key, or `null`.
 *
 * The column is a `varchar`, so a key written by a future migration this build does not know
 * about must not reach a client as a colour with no CSS token behind it — the same reason
 * `asLocale` exists. Lives beside the enum rather than in a feature module, because every
 * reader of the column needs it: the board, the list, the profile and the session.
 */
export function asManagerColor(value: string | null | undefined): ManagerColor | null {
  const parsed = managerColorSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * What a reader sees **in the audit trail**. `None` when unassigned.
 *
 * Audit rows carry display values stamped at write time, in one language, so that the history
 * does not get rewritten under a reader who switched language. The *selector* reads `colour.*`
 * from the web catalogue instead — see `specs/i18n.md`.
 */
export function managerColorLabel(key: string | null | undefined): string {
  return MANAGER_COLORS.find((color) => color.key === key)?.label ?? "None";
}

/**
 * How much of the owner's colour reaches the surface.
 *
 * One fixed strength per theme rather than per colour: that is what turns contrast into a
 * property of a few checkable combinations instead of a judgement call per assignment. Dark
 * takes more because the same proportion over a dark card reads as a smudge.
 *
 * These are the numbers the palette was sized against, and `manager-colors.test.ts` reads
 * the **stylesheet's** copies rather than these — so the two cannot drift without failing.
 */
export const TINT_STRENGTH = {
  /** The board card's surface. */
  card: { light: 24, dark: 30 },
  /** The detail modal's header block, which is meant to read as the colour's legend. */
  header: { light: 28, dark: 36 },
} as const;

/**
 * The surface tokens live in `apps/web/src/index.css` and are **not** mirrored here.
 *
 * They were, briefly, with a comment claiming a test kept the mirror honest. Nothing did:
 * the dark secondary-text value was wrong on the day it was written, in the direction that
 * hides failures. `manager-colors.test.ts` now parses the stylesheet instead — the file the
 * browser actually reads.
 */

/** Assignment carries the version it was read at — a colour change is a write like any other. */
export const updateUserColorSchema = z.object({
  /** `null` clears it: a manager may go back to having no colour. */
  color: managerColorSchema.nullable(),
  version: z.number().int(),
});
export type UpdateUserColorInput = z.infer<typeof updateUserColorSchema>;
